import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  accountType,
  onboardingCompleteInput,
  usStateCode,
  type OnboardingCompleteInput,
} from '@ballast/shared/schemas';

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:8787';

type UboInput = OnboardingCompleteInput['ubos'][number];
type UploadInput = OnboardingCompleteInput['uploads'][number];

function blankUbo(): UboInput {
  return {
    fullName: '',
    dateOfBirth: '',
    ownershipPct: 100,
    address: { street: '', city: '', state: 'NY', zip: '' },
  };
}

async function fileSha256(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function OnboardingCompletePage() {
  const params = useParams({ strict: false }) as { token?: string };
  const navigate = useNavigate();
  const token = params.token ?? '';

  const [ein, setEin] = useState('');
  const [formationState, setFormationState] = useState('NY');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [ubos, setUbos] = useState<UboInput[]>([blankUbo()]);
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountTypeValue, setAccountTypeValue] = useState<'checking' | 'savings'>('checking');
  const [uploads, setUploads] = useState<UploadInput[]>([]);
  const [agreementAccepted, setAgreementAccepted] = useState(false);

  const [serverError, setServerError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submittedRef, setSubmittedRef] = useState<string | null>(null);

  async function handleFileChange(
    kind: UploadInput['kind'],
    fileList: FileList | null,
  ) {
    if (!fileList || fileList.length === 0) return;
    const file = fileList[0];
    if (!file) return;
    const sha = await fileSha256(file);
    // R2 wiring is deferred. Until ballast-kyb-uploads is created and
    // the binding lands, we generate a placeholder r2Key from a
    // client-side UUID + filename. Backend accepts opaque keys.
    const r2Key = `kyb/${token}/${kind}/${crypto.randomUUID()}-${file.name}`;
    setUploads((prev) => [
      ...prev.filter((u) => u.kind !== kind),
      { kind, r2Key, sha256: sha, fileName: file.name, contentType: file.type || 'application/octet-stream' },
    ]);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setServerError(null);
    setValidationErrors([]);

    const payload: OnboardingCompleteInput = {
      ein,
      formationState: formationState as OnboardingCompleteInput['formationState'],
      primaryContact: { fullName: contactName, email: contactEmail, phone: contactPhone },
      ubos,
      bankAccount: { routingNumber, accountNumber, accountType: accountTypeValue },
      uploads,
      agreementAccepted: true,
    };

    const parsed = onboardingCompleteInput.safeParse(payload);
    if (!parsed.success) {
      setValidationErrors(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/v1/onboarding/complete/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed.data),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? `Submission failed (${res.status})`);
      }
      const body = (await res.json()) as { ok: true; partnerKybId: string };
      setSubmittedRef(body.partnerKybId.slice(0, 8));
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (!token || token.length < 36) {
    return (
      <main className="max-w-xl mx-auto px-6 py-16">
        <h1 className="text-2xl font-semibold text-brand-primary mb-2">Invalid link</h1>
        <p className="text-brand-muted">
          This onboarding link doesn&apos;t look right. Check the URL or contact support.
        </p>
      </main>
    );
  }

  if (submittedRef) {
    return (
      <main className="max-w-xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-semibold text-brand-primary mb-4">Thanks — under review</h1>
        <p className="text-brand-body mb-2">
          Reference: <code className="bg-white px-2 py-1 rounded">{submittedRef}</code>
        </p>
        <p className="text-brand-muted">
          We&apos;ve passed your information to our payments partner. Review typically takes 1–3
          business days; we&apos;ll email you the result either way.
        </p>
        <p className="mt-4">
          <button
            type="button"
            onClick={() => navigate({ to: '/login' })}
            className="text-brand-primary underline"
          >
            Sign in
          </button>
        </p>
      </main>
    );
  }

  return (
    <main className="max-w-2xl mx-auto px-6 py-12">
      <h1 className="text-3xl font-semibold text-brand-primary mb-2">Complete your application</h1>
      <p className="text-brand-muted mb-8">
        A few more details so our payments partner can verify your business. All information is
        encrypted and used only for compliance.
      </p>

      <form onSubmit={onSubmit} className="space-y-8" noValidate>
        <Section title="Business">
          <Field label="EIN (e.g. 12-3456789)">
            <input
              type="text"
              value={ein}
              onChange={(e) => setEin(e.target.value)}
              className="input"
              placeholder="12-3456789"
              required
            />
          </Field>
          <Field label="State of formation">
            <select
              value={formationState}
              onChange={(e) => setFormationState(e.target.value)}
              className="input"
            >
              {usStateCode.options.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
        </Section>

        <Section title="Primary contact">
          <Field label="Full name">
            <input
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              className="input"
              autoComplete="name"
              required
            />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Email">
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                className="input"
                autoComplete="email"
                required
              />
            </Field>
            <Field label="Phone">
              <input
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                className="input"
                autoComplete="tel"
                placeholder="(212) 555-0100"
                required
              />
            </Field>
          </div>
        </Section>

        <Section title="Owners (anyone with 25%+ ownership)">
          {ubos.map((ubo, idx) => (
            <div key={idx} className="border border-gray-200 rounded-md p-4 space-y-3 bg-white">
              <div className="flex justify-between items-baseline">
                <span className="text-sm font-medium text-brand-body">Owner {idx + 1}</span>
                {ubos.length > 1 && (
                  <button
                    type="button"
                    className="text-xs text-brand-muted underline"
                    onClick={() => setUbos((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    Remove
                  </button>
                )}
              </div>
              <Field label="Full name">
                <input
                  type="text"
                  value={ubo.fullName}
                  onChange={(e) =>
                    setUbos((prev) =>
                      prev.map((u, i) => (i === idx ? { ...u, fullName: e.target.value } : u)),
                    )
                  }
                  className="input"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date of birth">
                  <input
                    type="date"
                    value={ubo.dateOfBirth}
                    onChange={(e) =>
                      setUbos((prev) =>
                        prev.map((u, i) =>
                          i === idx ? { ...u, dateOfBirth: e.target.value } : u,
                        ),
                      )
                    }
                    className="input"
                  />
                </Field>
                <Field label="Ownership %">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={ubo.ownershipPct}
                    onChange={(e) =>
                      setUbos((prev) =>
                        prev.map((u, i) =>
                          i === idx ? { ...u, ownershipPct: Number(e.target.value) } : u,
                        ),
                      )
                    }
                    className="input"
                  />
                </Field>
              </div>
            </div>
          ))}
          {ubos.length < 4 && (
            <button
              type="button"
              onClick={() => setUbos((prev) => [...prev, blankUbo()])}
              className="text-sm text-brand-primary underline"
            >
              + Add another owner
            </button>
          )}
        </Section>

        <Section title="Bank account (for daily settlement)">
          <Field label="Routing number">
            <input
              type="text"
              inputMode="numeric"
              value={routingNumber}
              onChange={(e) => setRoutingNumber(e.target.value)}
              className="input"
              maxLength={9}
              required
            />
          </Field>
          <Field label="Account number">
            <input
              type="text"
              inputMode="numeric"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              className="input"
              required
            />
          </Field>
          <Field label="Account type">
            <select
              value={accountTypeValue}
              onChange={(e) => setAccountTypeValue(e.target.value as 'checking' | 'savings')}
              className="input"
            >
              {accountType.options.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
        </Section>

        <Section title="Documents">
          <Field label="Government-issued ID (front)">
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileChange('gov_id_front', e.target.files)}
            />
          </Field>
          <Field label="Proof of business address">
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileChange('proof_of_address', e.target.files)}
            />
          </Field>
        </Section>

        <label className="flex items-start gap-3 text-sm text-brand-body">
          <input
            type="checkbox"
            checked={agreementAccepted}
            onChange={(e) => setAgreementAccepted(e.target.checked)}
            className="mt-1"
            required
          />
          <span>
            I agree to the Ballast Merchant Services Agreement and confirm the information above is
            accurate.
          </span>
        </label>

        {validationErrors.length > 0 && (
          <ul className="text-red-700 text-sm bg-red-50 border border-red-200 rounded p-3 space-y-1">
            {validationErrors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}

        {serverError && (
          <p className="text-red-700 text-sm bg-red-50 border border-red-200 rounded p-3">
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || !agreementAccepted}
          className="w-full sm:w-auto bg-brand-accent hover:brightness-95 disabled:opacity-60 text-white px-8 py-3 rounded-md font-medium transition"
        >
          {submitting ? 'Submitting...' : 'Submit for review'}
        </button>
      </form>
    </main>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-brand-primary border-b border-gray-200 pb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-brand-body mb-1">{label}</span>
      {children}
    </label>
  );
}
